/**
 * TallyStatusBar — inline settings strip rendered just above the chat composer.
 *
 * Surfaces the user's current Tally connection (URL, Company, Bank Ledger),
 * a quick link to Mapping Rules, the live credit balance, and a pencil that
 * opens the full Settings modal. Each chip is click-to-edit inline (Enter to
 * save, Esc to cancel). All writes hit /tally-settings → DB.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Link as LinkIcon, Building2, Banknote, Wallet, Plus, Pencil,
  Table, Check, Loader2,
} from 'lucide-react';
import clsx from 'clsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '@/lib/hooks';
import { loadSettingsFromDB, saveSettingsToDB } from '@/lib/settings';
import { billingApi, readBalance, currencySymbol } from '@/lib/billing';
import { pingTally, getGlobalRules } from '@/lib/aiaccountant';

interface Props {
  onOpenSettings:    () => void;
  onOpenRecharge:    () => void;
  onOpenMappingRules:() => void;
}

type FieldKey = 'tally_url' | 'company_id' | 'bank_ledger';

export function TallyStatusBar({ onOpenSettings, onOpenRecharge, onOpenMappingRules }: Props) {
  const [settings, update] = useSettings();
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from DB once on mount (matches Settings modal behaviour)
  useEffect(() => {
    let mounted = true;
    loadSettingsFromDB().then((s) => {
      if (!mounted) return;
      update(s);
      setHydrated(true);
    });
    return () => { mounted = false; };
  }, [update]);

  // Live Tally ping (small dot on URL chip)
  const [pingState, setPingState] = useState<'idle'|'online'|'offline'>('idle');
  useEffect(() => {
    if (!settings.tally_url) { setPingState('idle'); return; }
    let cancelled = false;
    let t: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      const r = await pingTally(settings.tally_url);
      if (cancelled) return;
      setPingState(r.online ? 'online' : 'offline');
      t = setTimeout(tick, r.online ? 60_000 : 120_000);
    };
    tick();
    return () => { cancelled = true; if (t) clearTimeout(t); };
  }, [settings.tally_url]);

  // Balance
  const { data: balance } = useQuery({
    queryKey: ['billing-balance'],
    queryFn: () => billingApi.getBalance(),
    refetchInterval: 60_000,
    retry: false,
  });

  // Mapping rules count (Global)
  const { data: globalRulesCount } = useQuery({
    queryKey: ['global-rules-count'],
    queryFn: async () => {
      try { return (await getGlobalRules()).filter(r => r.enabled).length; }
      catch { return 0; }
    },
    refetchInterval: 5 * 60_000,
    retry: false,
  });

  return (
    <div className="border-t border-border bg-muted/30 px-3 py-1.5 flex items-center gap-1.5 overflow-x-auto">
      <SettingChip
        icon={<LinkIcon className="w-3 h-3" />}
        label="Tally URL"
        placeholder="https://abc.ngrok-free.app"
        value={settings.tally_url}
        fieldKey="tally_url"
        afterSave={(v) => update({ tally_url: v })}
        pingDot={settings.tally_url ? pingState : null}
        hydrated={hydrated}
      />
      <SettingChip
        icon={<Building2 className="w-3 h-3" />}
        label="Company"
        placeholder="e.g. P Shruthi"
        value={settings.company_id}
        fieldKey="company_id"
        afterSave={(v) => update({ company_id: v })}
        hydrated={hydrated}
      />
      <SettingChip
        icon={<Banknote className="w-3 h-3" />}
        label="Bank Ledger"
        placeholder="e.g. HDFC-1234"
        value={settings.bank_ledger}
        fieldKey="bank_ledger"
        afterSave={(v) => update({ bank_ledger: v })}
        hydrated={hydrated}
      />

      <Divider />

      <button
        onClick={onOpenMappingRules}
        className="flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
        title="Open mapping rules editor (Global + per-company)"
      >
        <Table className="w-3 h-3" />
        <span>Mapping Rules</span>
        {typeof globalRulesCount === 'number' && (
          <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-muted text-[10px] tabular-nums">
            {globalRulesCount}
          </span>
        )}
      </button>

      {/* Spacer pushes balance + pencil to the right */}
      <div className="flex-1" />

      <BalancePill balance={balance} onClick={onOpenRecharge} />
      <button
        onClick={onOpenRecharge}
        className="flex items-center gap-1 px-2 py-1 rounded text-xs hover:bg-primary/10 text-primary transition-colors shrink-0"
        title="Add credits"
      >
        <Plus className="w-3 h-3" />
        <span className="hidden sm:inline">Add credits</span>
      </button>

      <button
        onClick={onOpenSettings}
        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
        title="Open full settings"
        aria-label="Open settings"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ── Inline-editable chip ────────────────────────────────────────────────────

function SettingChip({
  icon, label, placeholder, value, fieldKey, afterSave, pingDot, hydrated,
}: {
  icon: React.ReactNode;
  label: string;
  placeholder: string;
  value: string;
  fieldKey: FieldKey;
  afterSave: (v: string) => void;
  pingDot?: 'idle'|'online'|'offline'|null;
  hydrated: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep draft in sync if external value changes while not editing
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed === value) { setEditing(false); return; }
    setSaving(true);
    try {
      const saved = await saveSettingsToDB({ [fieldKey]: trimmed });
      afterSave((saved as unknown as Record<string, string>)[fieldKey] ?? trimmed);
      // Mapping rules count, company config etc. may have changed downstream
      qc.invalidateQueries({ queryKey: ['global-rules-count'] });
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1200);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  };

  const empty = !value;
  const dotColour = pingDot === 'online' ? 'bg-green-500'
                   : pingDot === 'offline' ? 'bg-amber-500'
                   : pingDot === 'idle' ? 'bg-muted-foreground/40'
                   : null;

  return (
    <button
      onClick={() => !editing && setEditing(true)}
      className={clsx(
        'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors shrink-0 max-w-[260px]',
        editing
          ? 'bg-card border border-primary'
          : empty
          ? 'bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200'
          : 'bg-card border border-border hover:border-primary/50',
      )}
      title={editing ? '' : empty ? `Click to set ${label}` : `${label}: ${value} — click to edit`}
    >
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">{label}</span>

      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
          onClick={(e) => e.stopPropagation()}
          placeholder={placeholder}
          className="bg-transparent outline-none text-xs min-w-[120px] max-w-[220px]"
        />
      ) : (
        <span
          className={clsx(
            'truncate max-w-[180px]',
            empty && 'italic opacity-70',
          )}
        >
          {hydrated ? (value || `Set ${label}`) : '…'}
        </span>
      )}

      {dotColour && !editing && (
        <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0 ml-0.5', dotColour)} />
      )}
      {saving && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
      {justSaved && !saving && <Check className="w-3 h-3 text-green-600" />}
    </button>
  );
}

// ── Balance pill (compact, shows on the strip) ──────────────────────────────

function BalancePill({
  balance, onClick,
}: {
  balance: Parameters<typeof readBalance>[0] | undefined;
  onClick: () => void;
}) {
  const hasData = !!balance;
  const credits = hasData ? readBalance(balance ?? null) : 0;
  const symbol = hasData ? currencySymbol(balance ?? null) : '₹';
  const depleted = hasData && credits <= 0;
  const low = hasData && credits > 0 && credits <= 49;

  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium tabular-nums transition-colors shrink-0',
        !hasData
          ? 'bg-muted text-muted-foreground hover:bg-muted/80'
          : depleted
          ? 'bg-destructive/15 text-destructive hover:bg-destructive/25'
          : low
          ? 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25'
          : 'bg-primary/10 text-primary hover:bg-primary/15',
      )}
      title={depleted ? 'Balance depleted — recharge' : low ? 'Low balance' : 'Available balance'}
    >
      <Wallet className="w-3 h-3" />
      {symbol}
      {hasData
        ? credits.toLocaleString('en-IN', {
            minimumFractionDigits: 2, maximumFractionDigits: 2,
          })
        : '—'}
    </button>
  );
}

function Divider() {
  return <span className="w-px h-5 bg-border mx-0.5 shrink-0" />;
}
